//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import angular, { IWindowService } from 'angular';
import { PojoSet } from 'pojo-sets';

import UserId from '@models/UserId';

import {
  IBaseUrls,
  ICuralateGroup,
  ICuralateSession,
  ICuralateUser,
} from '../basic-session/ICuralateSession';
import { crl8, CURALATE } from '../curalate.globals';

// Exposes this.session variables that are initialized on page load to various angular components (i.e. userTimeZone)
// Currently this is simply a proxy to a global crl8.session object (for legacy code),
// but in the future we can make it the one and only broker of this.session variables.

export class SessionVarsService {
  private readonly session: ICuralateSession;

  constructor(private readonly $window: IWindowService) {
    if (!crl8?.session) {
      throw new Error(
        'window.crl8.session is not defined. Check Jsthis.sessionVars render snippet',
      );
    }

    this.session = crl8.session;
  }

  public getBaseUrls(): IBaseUrls {
    return this.session.baseUrls;
  }
  public getBaseUrl(serviceIdentifier: keyof IBaseUrls): string {
    return this.session.baseUrls[serviceIdentifier];
  }
  public getClientId(): string {
    return this.session.client.id;
  }
  public getClientName(): string {
    return this.session.client.name;
  }
  public getElevatedUntil(): string {
    return this.session.internalInfo.elevatedUntil;
  }
  public getFeatureViewName(): string {
    return this.session.internalInfo.featureViewName;
  }
  public getGroup(): ICuralateGroup {
    return this.session.group;
  }
  public getGroupName(): string {
    return this.session.group.name;
  }
  public getGroupsAndBrands(): ICuralateGroup[] {
    return this.session.user.allGroupsAndBrands;
  }
  public getEmailMD5Hash(): string {
    return this.session.user.emailMD5Hash;
  }
  public getPermissions(): PojoSet<string> {
    return this.session.permissions;
  }
  public getPinterestUsername(): string {
    return this.session.pinterestUsername;
  }
  public getPinterestUserId(): string {
    return this.session.pinterestUserId;
  }
  public getTimeZoneId(): string {
    return this.session.timeZone.id;
  }
  public getTimeZoneFullName(): string {
    return this.session.timeZone.fullName;
  }
  public getTimeZoneShortName(): string {
    return this.session.timeZone.shortName;
  }
  public getTimeZoneOffset(): number {
    return this.session.timeZone.offsetMinutes;
  }
  public getUserId(): UserId {
    return this.session.user.id;
  }
  public getUser(): ICuralateUser {
    return this.session.user;
  }
  public isCuralateEmployee(): boolean {
    return this.session.internalInfo.isCuralateEmployee;
  }
  public isFeatureEnabled(featureName: FeatureNames): boolean {
    return !!this.session.features[featureName];
  }
  public isMobile(): boolean {
    return false; /* hardcoded to false on Desktop */
  }
  public isSyndicationClient(): boolean {
    return this.session.client.isSyndication;
  }
  public isShareV2VendorClient(): boolean {
    return this.session.client.isShareV2Vendor;
  }
  public isFacebookReview(): boolean {
    return this.session.internalInfo.isFacebookAppReview;
  }
  public isUserAuthorizedForRole(role: string): boolean {
    return !!this.session.userRoles[role];
  }
  public isUserElevated(): boolean {
    return this.session.internalInfo.isUserElevated;
  }
  public canUserSetFeatureView(): boolean {
    return this.session.internalInfo.canUserSetFeatureView;
  }

  public getClientIdFromCookie(): number | null {
    const matched = /crl8.clntid=.[^;]*/.exec(document.cookie);
    if (matched) {
      const cookie = matched[0].split('=');
      return parseInt(cookie[1] ?? '', 10);
    }

    return null;
  }
  public changeClient(newClientId: number): void {
    return CURALATE.misc.Components.changeClient(newClientId);
  }

  public getHideGroups(): boolean {
    return this.$window.localStorage.getItem('crl8HideGroups') === 'true';
  }
  public setHideGroups(value: boolean): void {
    return this.$window.localStorage.setItem(
      'crl8HideGroups',
      value.toString(),
    );
  }
}

angular
  .module('curalate')
  .service('SessionVarsService', ['$window', SessionVarsService]);
